import { queryMarketListing } from '@wix/ambassador-devcenter-app-market-listing-v1-market-listing/http';
import type {
  MarketListing,
  QueryMarketListingResponse,
} from '@wix/ambassador-devcenter-app-market-listing-v1-market-listing/types';
import { type HttpResponse, type HttpClient } from '@wix/http-client';
import {
  AppMarketListingsServiceErrorCode,
  createAppMarketListingsServiceError,
} from './errors';
import { chooseAppListingToSaveByStatus } from './utils';

type QueryMarketListingError = {
  name: string;
  message: string;
};

export type GetAppMarketListingParams = {
  appDefinitionIds: string[];
  languageCode?: string;
  pagingLimit?: number;
};

const getAppMarketListing = (
  httpClient: HttpClient,
  params: GetAppMarketListingParams,
): Promise<
  HttpResponse<QueryMarketListingResponse | QueryMarketListingError>
> => {
  return httpClient.request(
    queryMarketListing({
      query: {
        filter: {
          appId: params.appDefinitionIds,
          languageCode: params.languageCode || 'en',
        },
        cursorPaging: {
          limit: params.pagingLimit,
        },
      },
    }),
  );
};

const getAppMarketListingByCursor = (
  httpClient: HttpClient,
  cursor: string,
  limit: number = 100,
): Promise<
  HttpResponse<QueryMarketListingResponse | QueryMarketListingError>
> => {
  return httpClient.request(
    queryMarketListing({
      query: {
        cursorPaging: { cursor, limit },
      },
    }),
  );
};

const validateMarketListingResponse = (
  data: QueryMarketListingResponse | undefined,
) => data?.marketListing && data?.metadata;

const getAppMarketDataWithDefaults = (
  response: HttpResponse<QueryMarketListingResponse | QueryMarketListingError>,
): QueryMarketListingResponse => {
  const isCorrectMarketListingResponse = validateMarketListingResponse(
    response?.data as QueryMarketListingResponse | undefined,
  );

  if (isCorrectMarketListingResponse) {
    return response.data as QueryMarketListingResponse;
  }

  return {
    marketListing: [],
    metadata: {
      cursors: {},
    },
  };
};

const createGetAppMarketListingsErrorHandler =
  (params: GetAppMarketListingParams) => (e: Error) => {
    throw createAppMarketListingsServiceError(
      AppMarketListingsServiceErrorCode.FailedToQueryMarketListing,
    )
      .withParentError(e)
      .withAppDefinitionIds(params.appDefinitionIds);
  };

export const getAllAppMarketListings = async (
  httpClient: HttpClient,
  params: GetAppMarketListingParams,
): Promise<MarketListing[]> => {
  const errorHandler = createGetAppMarketListingsErrorHandler(params);

  const initialData = await getAppMarketListing(httpClient, params)
    .then(getAppMarketDataWithDefaults)
    .catch(errorHandler);

  const res = [...(initialData.marketListing || [])];

  let cursor = initialData.metadata?.cursors?.next;

  while (cursor) {
    const newData = await getAppMarketListingByCursor(
      httpClient,
      cursor,
      params.pagingLimit,
    )
      .then(getAppMarketDataWithDefaults)
      .catch(errorHandler);
    res.push(...(newData.marketListing || []));
    cursor = newData.metadata?.cursors?.next;
  }

  return res;
};

export const getAppsInfoByLanguageWithDefaults = async (
  httpClient: HttpClient,
  appDefinitionIds: string[],
  languageCode: string,
): Promise<Record<string, MarketListing>> => {
  const listings = await getAllAppMarketListings(httpClient, {
    appDefinitionIds,
    languageCode,
  });

  const appsInfo = listings.reduce<Record<string, MarketListing>>(
    (acc, listing) => {
      acc[listing.appId!] = chooseAppListingToSaveByStatus(
        listing,
        acc[listing.appId!],
      );
      return acc;
    },
    {},
  );

  const missingApps = appDefinitionIds.filter((appId) => !appsInfo[appId]);

  if (!missingApps.length) {
    return appsInfo;
  }

  const missingListings = await getAllAppMarketListings(httpClient, {
    appDefinitionIds: missingApps,
    languageCode: 'en',
  });

  missingListings.forEach((listing) => {
    appsInfo[listing.appId!] = chooseAppListingToSaveByStatus(
      listing,
      appsInfo[listing.appId!],
    );
  });

  return appsInfo;
};

export const fetchAppsNames = async (
  httpClient: HttpClient,
  appDefIds: string[],
  languageCode: string = 'en',
): Promise<Record<string, string>> => {
  const listings = await getAllAppMarketListings(httpClient, {
    appDefinitionIds: appDefIds,
    languageCode,
  });

  return Array.isArray(listings)
    ? listings.reduce((appsDict: Record<string, string>, currentApp) => {
        if (currentApp.appId && currentApp.basicInfo?.name) {
          appsDict[currentApp.appId] = currentApp.basicInfo.name;
        }
        return appsDict;
      }, {})
    : {};
};
